import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { ClipboardList, ArrowRight, Calendar } from "lucide-react";

import {
  getOperativo,
  listFases,
  transitionFase,
  type Operativo,
  type OperativoFase,
} from "@/api/operativos";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

const NEXT_FASE: Record<string, string[]> = {
  programado: ["en_curso", "cancelado"],
  en_curso: ["hit_detectado", "cerrado", "cancelado"],
  hit_detectado: ["aseguramiento", "en_curso", "cancelado"],
  aseguramiento: ["puesta_disposicion", "en_curso", "cancelado"],
  puesta_disposicion: ["cerrado", "cancelado"],
  cerrado: [],
  cancelado: [],
};

export function OperativoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const operativoId = Number(id);
  const qc = useQueryClient();
  const toast = useToast();
  const [comentario, setComentario] = useState("");
  const [target, setTarget] = useState<string>("");

  const op = useQuery({
    queryKey: ["operativo", operativoId],
    queryFn: () => getOperativo(operativoId),
    enabled: Number.isFinite(operativoId),
  });
  const fases = useQuery({
    queryKey: ["operativo-fases", operativoId],
    queryFn: () => listFases(operativoId),
    enabled: Number.isFinite(operativoId),
  });

  const transition = useMutation({
    mutationFn: () =>
      transitionFase(operativoId, { faseNueva: target, comentario: comentario || undefined }),
    onSuccess: () => {
      toast.push("Transición aplicada", "success");
      setComentario("");
      setTarget("");
      qc.invalidateQueries({ queryKey: ["operativo", operativoId] });
      qc.invalidateQueries({ queryKey: ["operativo-fases", operativoId] });
    },
    onError: () => toast.push("Transición rechazada", "error"),
  });

  if (!Number.isFinite(operativoId)) {
    return <div className="p-6 text-muted-200">Operativo inválido.</div>;
  }

  return (
    <div className="p-6 space-y-4" data-testid={`page-operativo-${operativoId}`}>
      <header className="flex items-center gap-3">
        <ClipboardList size={20} />
        <h1 className="text-2xl font-semibold">{op.data?.nombre ?? `Operativo #${operativoId}`}</h1>
        {op.data && (
          <span className="rounded-full bg-primary/15 text-primary-400 px-2 py-0.5 text-xs">
            {op.data.fase}
          </span>
        )}
      </header>

      {op.data && <OperativoMeta op={op.data} />}

      <section className="card">
        <h2 className="font-medium text-sm mb-2 flex items-center gap-2">
          <ArrowRight size={14} /> Transición de fase
        </h2>
        <div className="grid md:grid-cols-3 gap-2">
          <select
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="op-fase-select"
          >
            <option value="">Selecciona…</option>
            {(NEXT_FASE[op.data?.fase ?? ""] ?? []).map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <input
            className="input md:col-span-2"
            placeholder="Comentario (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            data-testid="op-comentario"
          />
        </div>
        <div className="mt-2 flex justify-end">
          <button
            className="btn-primary"
            disabled={!target || transition.isPending}
            onClick={() => transition.mutate()}
            data-testid="op-transition"
          >
            Aplicar transición
          </button>
        </div>
      </section>

      <FasesTimeline fases={fases.data ?? []} />
    </div>
  );
}

function OperativoMeta({ op }: { op: Operativo }) {
  return (
    <section className="card">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Field label="Programado">
          {op.programadoInicio ? formatDate(op.programadoInicio) : "—"}
        </Field>
        <Field label="En curso">{op.enCursoInicio ? formatDate(op.enCursoInicio) : "—"}</Field>
        <Field label="Aseguramiento">
          {op.aseguramientoInicio ? formatDate(op.aseguramientoInicio) : "—"}
        </Field>
        <Field label="Cerrado">{op.cerradoInicio ? formatDate(op.cerradoInicio) : "—"}</Field>
      </dl>
      {op.descripcion && (
        <p className="mt-3 text-sm text-foreground">{op.descripcion}</p>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-200">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}

function FasesTimeline({ fases }: { fases: OperativoFase[] }) {
  return (
    <section className="card">
      <header className="mb-3 flex items-center gap-2">
        <Calendar size={16} />
        <h2 className="font-medium text-sm">Timeline de fases</h2>
      </header>
      {fases.length === 0 ? (
        <p className="text-sm text-muted-200">Sin transiciones registradas.</p>
      ) : (
        <ol className="space-y-3">
          {fases.map((f) => (
            <li key={f.id} className="flex gap-3 items-start" data-testid={`fase-${f.id}`}>
              <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
              <div className="flex-1">
                <div className="text-sm font-medium">
                  {f.faseAnterior ? `${f.faseAnterior} → ` : ""}
                  {f.faseNueva}
                </div>
                <div className="text-xs text-muted-200">
                  {formatDate(f.at)} · usuario {f.usuarioId}
                </div>
                {f.comentario && (
                  <div className="text-sm mt-1 text-foreground">{f.comentario}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
